import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaFutbol, FaChartLine, FaTrophy, FaTimes } from 'react-icons/fa';

const LandingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-premier-purple to-premier-pink">
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <motion.h1 
          className="text-5xl font-bold text-white text-center"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Premier Predictor
        </motion.h1>
        <motion.p 
          className="text-white mb-8 text-lg text-center"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          The ultimate Premier League prediction challenge
        </motion.p>
        
        <motion.div 
          className="bg-white p-8 rounded-lg shadow-lg mb-12"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h2 className="text-3xl font-semibold mb-4 text-premier-purple text-center">Think You Know the Premier League? Prove It!</h2>
          <p className="text-gray-700 mb-6 text-lg text-center">
            Take on the Ultimate Prediction Challenge: Predict individual matches and/or the entire league table! Climb the leaderboard, compete with friends, and show off your Premier League expertise!
          </p>
          <div className="text-center">
            <button
              onClick={openModal}
              className="bg-premier-purple text-white px-6 py-2 rounded-full text-lg font-semibold hover:bg-premier-pink transition duration-300 ease-in-out"
            >
              How to Play
            </button>
          </div>
        </motion.div>

        <motion.div 
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h2 className="text-3xl font-bold mb-6 text-white text-center">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <FaFutbol />, title: "Make Your Predictions", description: "Set your predictions for all 20 Premier League teams and 380 games." },
              { icon: <FaChartLine />, title: "Track Progress", description: "Watch as the actual table and results unfold throughout the season." },
              { icon: <FaTrophy />, title: "Win Bragging Rights", description: "Rack up the points and compete against your friends and family!" },
            ].map((step, index) => (
              <motion.div 
                key={index}
                className="bg-white p-6 rounded-lg shadow-md text-center flex flex-col items-center"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
              >
                <div className="text-6xl mb-4 text-premier-purple">{step.icon}</div>
                <h3 className="text-xl font-semibold mb-2 text-premier-purple">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div 
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <h2 className="text-3xl font-bold mb-6 text-white text-center">Frequently Asked Questions</h2>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <details className="mb-4">
              <summary className="text-lg font-semibold text-premier-purple cursor-pointer">How do I take part?</summary>
              <p className="mt-2 text-gray-700">The game is 100% free to play! All you need to do is create an account and verify your email address. </p>
            </details>
            <details className="mb-4">
              <summary className="text-lg font-semibold text-premier-purple cursor-pointer">How does the scoring work?</summary>
              <p className="mt-2 text-gray-700">Visit the <Link to="/scoring-guide" className="text-premier-purple font-bold underline hover:text-premier-pink">Scoring Guide</Link> for a breakdown of the scoring mechanism
              for both strands of the predictor game.</p>
            </details>
            <details className="mb-4">
              <summary className="text-lg font-semibold text-premier-purple cursor-pointer">Can I join after the season has started?</summary>
              <p className="mt-2 text-gray-700">You can sign-up to begin making match predictions during any gameweek of the season! Unfortunately, late registration for the 
                league predictor would give an unfair competitive advantage, so the deadline to submit a league prediction is 90 minutes before the opening game of the season.
              </p>
            </details>
            <details>
              <summary className="text-lg font-semibold text-premier-purple cursor-pointer">What is the architecture of the application?</summary>
              <p className="mt-2 text-gray-700">The application is built using a React JS frontend, a Python Flask backend and interacts with Fireabase
                for authentication and some data storage. For more information on how Firebase manages the authentication process, visit <a href="https://firebase.google.com/docs" className="text-premier-purple font-bold underline hover:text-premier-pink">the Firebase docs</a>.
              </p>
            </details>
          </div>
        </motion.div>

        <motion.div 
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
        >
          <h2 className="text-3xl font-bold mb-4 text-white">Ready to become the ultimate predictor?</h2>
          <Link 
            to="/login" 
            className="bg-white text-premier-purple px-8 py-3 rounded-full text-lg font-semibold hover:bg-premier-pink hover:text-white transition duration-300 ease-in-out inline-block"
          >
            Get Started Now
          </Link>
        </motion.div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-premier-purple">How to Play</h3>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <FaTimes size={24} />
              </button>
            </div>
            <div className="aspect-w-16 aspect-h-9 bg-gray-200 mb-4">
              {/* Placeholder for video */}
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">Video Explanation Coming Soon...</p>
              </div>
            </div>
            <p className="text-gray-700">
              This video will guide you through the process of making predictions and participating in the Premier Predictor challenge.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;